import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import InvoicePrintModal from '../../components/InvoicePrintModal';
import { useToast } from '../../context/ToastContext';

const SaleEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  // Invoice Head State
  const [saleNo, setSaleNo] = useState('');
  const [saleDate, setSaleDate] = useState(new Date().toISOString().slice(0, 10));
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showQuickAddCustModal, setShowQuickAddCustModal] = useState(false);
  const [quickCustName, setQuickCustName] = useState('');
  const [quickCustMobile, setQuickCustMobile] = useState('');
  const [quickCustAddress, setQuickCustAddress] = useState('');
  const [quickCustDisc, setQuickCustDisc] = useState(0);
  const [quickCustSaving, setQuickCustSaving] = useState(false);
  const [vehicleNo, setVehicleNo] = useState('');
  const [remark, setRemark] = useState('');

  // Line Items State
  const [items, setItems] = useState([]);
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  // Current Item Input Line State
  const [currentItem, setCurrentItem] = useState({
    productId: '',
    productName: '',
    noofbox: 0,
    kgperbox: 0,
    addqty: 0,
    disc_qty: 0,
    rate: 0
  });

  // Additional Charges & Adjustments
  const [discPercent, setDiscPercent] = useState(0);
  const [discAmount, setDiscAmount] = useState(0);
  const [hamaliUnit, setHamaliUnit] = useState(0);
  const [hamaliRate, setHamaliRate] = useState(0);
  const [iceUnit, setIceUnit] = useState(0);
  const [iceRate, setIceRate] = useState(0);
  const [transportCharges, setTransportCharges] = useState(0);
  const [expenses, setExpenses] = useState(0);
  const [paidAmt, setPaidAmt] = useState(0);

  // Customer Financial Balances
  const [previousBalance, setPreviousBalance] = useState(0);
  const [selectedCustomerObj, setSelectedCustomerObj] = useState(null);

  // Print Modal & Flags
  const [printData, setPrintData] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showProductList, setShowProductList] = useState(false);

  const customerDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);
  const productInputRef = useRef(null);
  const boxesInputRef = useRef(null);
  const rateInputRef = useRef(null);
  const [highlightedCustIndex, setHighlightedCustIndex] = useState(-1);
  const [highlightedProdIndex, setHighlightedProdIndex] = useState(-1);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerList(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowProductList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset form to blank and load next Sale No
  const resetFormToBlank = useCallback(async () => {
    try {
      const nextNoRes = await axiosClient.get('/sales/next-no');
      if (nextNoRes.success) {
        setSaleNo(nextNoRes.nextSaleNo);
      }
    } catch (e) {
      console.error('Error fetching next sale number:', e);
    }
    setSaleDate(new Date().toISOString().slice(0, 10));
    setCustomerId('');
    setCustomerSearch('');
    setCustomerMobile('');
    setSelectedCustomerObj(null);
    setVehicleNo('');
    setRemark('');
    setItems([]);
    setEditingItemIndex(null);
    setCurrentItem({
      productId: '',
      productName: '',
      noofbox: 0,
      kgperbox: 0,
      addqty: 0,
      disc_qty: 0,
      rate: 0
    });
    setDiscPercent(0);
    setDiscAmount(0);
    setHamaliUnit(0);
    setHamaliRate(0);
    setIceUnit(0);
    setIceRate(0);
    setTransportCharges(0);
    setExpenses(0);
    setPaidAmt(0);
    setPreviousBalance(0);
    setError('');

    if (id) {
      navigate('/sales/new', { replace: true });
    }
  }, [id, navigate]);

  // Fetch Dropdown options, Next Sale No, and existing sale if editing
  useEffect(() => {
    const loadInitData = async () => {
      try {
        const [custRes, prodRes, nextNoRes] = await Promise.all([
          axiosClient.get('/accounts?type=CUSTOMER'),
          axiosClient.get('/products'),
          axiosClient.get('/sales/next-no')
        ]);

        const custList = custRes.data || [];
        if (custRes.success) setCustomers(custList);
        if (prodRes.success) setProducts(prodRes.data || []);
        if (nextNoRes.success && !id) setSaleNo(nextNoRes.nextSaleNo);

        // If editing existing sale invoice
        if (id) {
          const saleRes = await axiosClient.get(`/sales/${id}`);
          if (saleRes.success) {
            const s = saleRes.data;
            const currentSaleNo = s.SaleNo ?? s.saleNo ?? '';
            setSaleNo(currentSaleNo);

            const dt = s.SaleDate ?? s.saleDate;
            setSaleDate(dt ? String(dt).slice(0, 10) : new Date().toISOString().slice(0, 10));

            const curCustId = s.CustomerId ?? s.customerId ?? '';
            setCustomerId(curCustId);
            setVehicleNo(s.VehicleNo || s.vehicleNo || '');
            setRemark(s.Remark || s.remark || '');
            setDiscPercent(parseFloat(s.DiscPercent ?? s.discPercent) || 0);
            setDiscAmount(parseFloat(s.DiscAmount ?? s.discAmount) || 0);
            setHamaliUnit(parseFloat(s.HamaliUnit) || 0);
            setHamaliRate(parseFloat(s.HamaliRate) || 0);
            setIceUnit(parseFloat(s.IceUnit) || 0);
            setIceRate(parseFloat(s.IceRate) || 0);
            setTransportCharges(parseFloat(s.TransportCharges) || 0);
            setExpenses(parseFloat(s.Expenses) || 0);
            setPaidAmt(parseFloat(s.PaidAmt ?? s.paidAmt) || 0);
            setPreviousBalance(parseFloat(s.previousBalance) || 0);

            // Set Customer Name textbox & mobile
            const matchedCust = custList.find((c) => String(c.Id) === String(curCustId));
            if (matchedCust) {
              setCustomerSearch(matchedCust.AccountName);
              setCustomerMobile(s.CustomerMobile || matchedCust.MobileNo || '');
              setSelectedCustomerObj(matchedCust);
            } else if (s.CustomerName) {
              setCustomerSearch(s.CustomerName);
              setCustomerMobile(s.CustomerMobile || '');
            }

            // Map and normalize line items with rates
            const formattedItems = (s.items || []).map((it) => {
              const r = parseFloat(it.Rate ?? it.rate ?? 0) || 0;
              const q = parseFloat(it.net_qty ?? it.Qty ?? 0) || 0;
              const t = parseFloat(it.total) || q * r;
              return {
                productId: it.productId || 0,
                productName: it.productName || '',
                kgperbox: parseFloat(it.kgperbox) || 0,
                noofbox: parseFloat(it.noofbox) || 0,
                addqty: parseFloat(it.addqty) || 0,
                Qty: parseFloat(it.Qty) || 0,
                disc_qty: parseFloat(it.disc_qty) || 0,
                net_qty: q,
                rate: r,
                total: t
              };
            });
            setItems(formattedItems);
          }
        }
      } catch (err) {
        console.error('Failed to load sale initialization data:', err);
      }
    };
    loadInitData();
  }, [id]);

  // Select customer from list view or modal
  const handleSelectCustomer = async (c) => {
    if (!c) {
      setCustomerId('');
      setCustomerSearch('');
      setCustomerMobile('');
      setSelectedCustomerObj(null);
      setPreviousBalance(0);
      return;
    }
    setCustomerId(c.Id);
    setCustomerSearch(c.AccountName);
    setCustomerMobile(c.MobileNo || '');
    setSelectedCustomerObj(c);
    setShowCustomerList(false);
    setShowCustomerModal(false);

    try {
      const res = await axiosClient.get(`/accounts/${c.Id}`);
      if (res.success) {
        const acc = res.data;
        setPreviousBalance(parseFloat(acc.financialSummary?.netBalance) || 0);
        if (acc.disc && parseFloat(acc.disc) > 0) {
          setDiscPercent(parseFloat(acc.disc));
        }
      }
    } catch (err) {
      console.error('Error fetching customer balance:', err);
    }
  };

  // Quick Add Customer directly from Sale Invoice
  const handleQuickAddCustomer = async (e) => {
    e.preventDefault();
    if (!quickCustName || !quickCustName.trim()) {
      toast.showError('Please enter Customer Name.');
      return;
    }

    setQuickCustSaving(true);
    try {
      const res = await axiosClient.post('/accounts', {
        accountName: quickCustName.trim(),
        accountType: 'CUSTOMER',
        mobileNo: quickCustMobile || '0',
        address: quickCustAddress || '',
        disc: parseFloat(quickCustDisc) || 0
      });

      if (res.success) {
        const newCust = res.data || {
          Id: res.accountId,
          AccountName: quickCustName.trim(),
          AccountType: 'CUSTOMER',
          MobileNo: quickCustMobile || '',
          Address: quickCustAddress || '',
          disc: parseFloat(quickCustDisc) || 0
        };

        setCustomers((prev) => [...prev, newCust]);
        await handleSelectCustomer(newCust);
        setCustomerMobile(newCust.MobileNo || '');
        toast.showSuccess(`Customer "${newCust.AccountName}" saved and selected!`);
        setShowQuickAddCustModal(false);
        setQuickCustName('');
        setQuickCustMobile('');
        setQuickCustAddress('');
        setQuickCustDisc(0);
      }
    } catch (err) {
      toast.showError(err.message || 'Error saving customer.');
    } finally {
      setQuickCustSaving(false);
    }
  };

  // Filtered customer list for list view
  const filteredCustomers = customers.filter((c) => {
    const term = (customerSearch || '').toLowerCase().trim();
    if (!term) return true;
    return (
      (c.AccountName || '').toLowerCase().includes(term) ||
      (c.MobileNo || '').toLowerCase().includes(term) ||
      (c.Address || '').toLowerCase().includes(term)
    );
  });

  // Filtered products list for live search dropdown
  const filteredProducts = products.filter((p) => {
    const term = (currentItem.productName || '').toLowerCase().trim();
    if (!term) return true;
    return (p.productName || '').toLowerCase().includes(term);
  });

  // Calculate live line item metrics
  const itemTotalWeight =
    (parseFloat(currentItem.kgperbox) || 0) * (parseFloat(currentItem.noofbox) || 0) +
    (parseFloat(currentItem.addqty) || 0);
  const itemNetQty = itemTotalWeight;
  const itemTotalAmount = itemNetQty * (parseFloat(currentItem.rate) || 0);

  // Add or Update Item in Line Table (auto-creates product if new)
  const handleAddItem = async (e) => {
    e.preventDefault();
    const pName = (currentItem.productName || '').trim();
    if (!pName) {
      alert('Please enter or select a product item.');
      return;
    }
    if (itemTotalWeight <= 0) {
      alert('Please enter valid quantities or boxes.');
      return;
    }

    let finalProductId = currentItem.productId;

    // If new product was entered directly, auto-save in product master!
    if (!finalProductId) {
      const matched = products.find(p => p.productName?.toLowerCase().trim() === pName.toLowerCase());
      if (matched) {
        finalProductId = matched.Id;
      } else {
        try {
          const res = await axiosClient.post('/products', {
            productName: pName,
            sRate: parseFloat(currentItem.rate) || 0,
            pRate: parseFloat(currentItem.rate) || 0,
            unit: 'KG'
          });
          if (res.success) {
            finalProductId = res.productId;
            const newProd = {
              Id: res.productId,
              productName: pName,
              SRate: parseFloat(currentItem.rate) || 0,
              PRate: parseFloat(currentItem.rate) || 0,
              unit: 'KG'
            };
            setProducts((prev) => [...prev, newProd]);
          }
        } catch (err) {
          console.error('Error auto-creating product:', err);
        }
      }
    }

    const newItem = {
      productId: finalProductId || 0,
      productName: pName,
      noofbox: parseFloat(currentItem.noofbox) || 0,
      kgperbox: parseFloat(currentItem.kgperbox) || 0,
      addqty: parseFloat(currentItem.addqty) || 0,
      Qty: itemTotalWeight,
      disc_qty: 0,
      net_qty: itemNetQty,
      rate: parseFloat(currentItem.rate) || 0,
      total: itemTotalAmount
    };

    if (editingItemIndex !== null) {
      const updated = [...items];
      updated[editingItemIndex] = newItem;
      setItems(updated);
      setEditingItemIndex(null);
    } else {
      setItems([...items, newItem]);
    }

    // Reset current item inputs
    setCurrentItem({
      productId: '',
      productName: '',
      noofbox: 0,
      kgperbox: 0,
      addqty: 0,
      disc_qty: 0,
      rate: 0
    });
    setShowProductList(false);
    setHighlightedProdIndex(-1);
    setTimeout(() => {
      productInputRef.current?.focus();
    }, 50);
  };

  // Load item into input row for editing
  const handleEditItem = (index) => {
    const it = items[index];
    setCurrentItem({
      productId: it.productId,
      productName: it.productName,
      noofbox: it.noofbox,
      kgperbox: it.kgperbox,
      addqty: it.addqty,
      disc_qty: 0,
      rate: it.rate
    });
    setEditingItemIndex(index);
  };

  // Remove Item
  const handleRemoveItem = (index) => {
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
    if (editingItemIndex === index) {
      setEditingItemIndex(null);
    }
  };

  // Subtotal Calculation
  const subTotal = items.reduce((sum, it) => sum + (parseFloat(it.total) || 0), 0);

  // Sync Hamali & Ice amounts
  const calculatedHamali = (parseFloat(hamaliRate) || 0) * (parseFloat(hamaliUnit) || 0);
  const calculatedIce = (parseFloat(iceRate) || 0) * (parseFloat(iceUnit) || 0);
  const calculatedDiscount = (subTotal * (parseFloat(discPercent) || 0)) / 100;

  const grandTotal =
    subTotal +
    calculatedHamali +
    calculatedIce +
    (parseFloat(transportCharges) || 0) +
    (parseFloat(expenses) || 0) -
    (parseFloat(discAmount) || calculatedDiscount);

  const netBalanceAfterSale = previousBalance + grandTotal - (parseFloat(paidAmt) || 0);

  // Handle Save
  const handleSaveSale = async () => {
    const trimmedCustName = (customerSearch || '').trim();
    if (!trimmedCustName) {
      setError('Please enter customer name.');
      return;
    }
    if (items.length === 0) {
      setError('Please add at least one item to the sale invoice.');
      return;
    }

    setError('');
    setSaving(true);

    try {
      let finalCustomerId = customerId;

      // If customer doesn't exist yet, auto-create customer in database!
      if (!finalCustomerId) {
        const matched = customers.find(
          (c) => c.AccountName?.trim().toLowerCase() === trimmedCustName.toLowerCase()
        );

        if (matched) {
          finalCustomerId = matched.Id;
        } else {
          // Auto create customer directly
          const createCustRes = await axiosClient.post('/accounts', {
            accountName: trimmedCustName,
            accountType: 'CUSTOMER',
            mobileNo: customerMobile || '0',
            address: '',
            disc: parseFloat(discPercent) || 0
          });

          if (createCustRes.success) {
            finalCustomerId = createCustRes.accountId;
            const newCust = createCustRes.data || {
              Id: createCustRes.accountId,
              AccountName: trimmedCustName,
              AccountType: 'CUSTOMER',
              MobileNo: customerMobile || '',
              disc: parseFloat(discPercent) || 0
            };
            setCustomers((prev) => [newCust, ...prev]);
            setSelectedCustomerObj(newCust);
          } else {
            throw new Error(createCustRes.message || 'Failed to auto-save new customer.');
          }
        }
      }

      const payload = {
        saleNo,
        saleDate,
        customerId: finalCustomerId,
        vehicleNo,
        items,
        discPercent,
        discAmount: parseFloat(discAmount) || calculatedDiscount,
        hamaliRate,
        hamaliUnit,
        hamaliCharges: calculatedHamali,
        iceRate,
        iceUnit,
        iceCharges: calculatedIce,
        transportCharges,
        expenses,
        paidAmt,
        remark
      };

      let savedSaleId = id;
      if (id) {
        await axiosClient.put(`/sales/${id}`, payload);
        toast.success(`Sale Invoice #${saleNo} updated successfully!`);
      } else {
        const res = await axiosClient.post('/sales', payload);
        savedSaleId = res.saleId;
        toast.success(`Sale Invoice #${saleNo} saved successfully!`);
      }

      // Check WhatsApp Settings for Print Popup & Auto-Send
      let waSettings = null;
      try {
        const waRes = await axiosClient.get('/whatsapp/settings');
        if (waRes.success && waRes.settings) {
          waSettings = waRes.settings;
        }
      } catch (waErr) {
        console.warn('Could not load WhatsApp settings for save flow:', waErr.message);
      }

      // Fetch invoice for print preview / modal
      const printRes = await axiosClient.get(`/sales/${savedSaleId}`);
      if (printRes.success) {
        const inv = printRes.data;
        setPrintData({
          ...inv,
          CustomerName: trimmedCustName,
          CustomerMobile: customerMobile || inv.CustomerMobile,
          netBalanceAfterSale
        });
        const shouldShowPopup = !waSettings || (waSettings.ShowPrintPopupSale !== 0 && waSettings.ShowPrintPopupSale !== false);
        if (shouldShowPopup) {
          setShowPrintModal(true);
        }
      }

      // Auto-send via WhatsApp if enabled in WhatsApp Settings
      try {
        if (waSettings && waSettings.IsEnabled && waSettings.AutoSendSale) {
          if (waSettings.SendMode === 'web') {
            // Open WhatsApp Web or Mobile WhatsApp App with pre-filled invoice
            const publicUrl = window.location.origin;
            const viewUrl = `${publicUrl}/api/whatsapp/view-invoice?type=sale&id=${saleNo}`;
            const firmName = waSettings.firmName || 'FISH SHOP';
            const msg = `🧾 *SALE INVOICE #${saleNo}*\n🏢 *${firmName}*\nCustomer: ${trimmedCustName}\nTotal Amount: ₹${parseFloat(grandTotal).toFixed(2)}\n📄 *View & Print Bill:*\n${viewUrl}\n${waSettings.CustomMessage ? '_' + waSettings.CustomMessage + '_' : '🙏 Thank you for your business!'}`;

            const targets = [];
            const custMob = (customerMobile || printRes.data?.CustomerMobile || '').replace(/\D/g, '');
            if (waSettings.SendToCustomer !== 0 && custMob && custMob.length >= 10) {
              targets.push(custMob.length === 10 ? '91' + custMob : custMob);
            }
            if (waSettings.SendToOwner === 1) {
              const rawOwn = String(waSettings.OwnerMobile || waSettings.firmDefaultContact || '');
              const ownerNums = rawOwn.split(/[,/;\n\s&]+/)
                .map((n) => n.replace(/\D/g, ''))
                .map((n) => (n.length === 12 && n.startsWith('91') ? n.slice(2) : (n.length === 11 && n.startsWith('0') ? n.slice(1) : n)))
                .filter((n) => n.length >= 10);
              ownerNums.forEach((n) => {
                const fullOwn = n.length === 10 ? '91' + n : n;
                if (!targets.includes(fullOwn)) targets.push(fullOwn);
              });
            }

            targets.forEach((phone, idx) => {
              const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(msg)}`;
              if (idx === 0) window.open(url, '_blank');
              else setTimeout(() => window.open(url, '_blank'), 1000);
            });
            toast.success('WhatsApp Web / App opened for sale invoice!');
          } else {
            // Send via API
            const sendRes = await axiosClient.post('/whatsapp/send-invoice', {
              saleId: savedSaleId,
              invoiceNo: saleNo,
              type: 'sale',
              partyName: trimmedCustName,
              phone: customerMobile || printRes.data?.CustomerMobile,
              checkSender: true
            });
            if (sendRes.success) {
              toast.success(sendRes.message || 'WhatsApp bill sent automatically!');
            }
          }
        }
      } catch (waErr) {
        console.warn('Auto-dispatch WhatsApp note:', waErr.message);
      }

      setSuccessMsg(`Sale Invoice #${saleNo} saved successfully! Form reloaded for next entry.`);
      setTimeout(() => setSuccessMsg(''), 5000);

      // Automatically reset form to blank ready for next sale bill
      await resetFormToBlank();
    } catch (err) {
      const errMsg = err.message || 'Error saving sale invoice.';
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Header Banner */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 pb-2 border-bottom">
        <div>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark">
              <i className="bi bi-receipt-cutoff text-primary me-2"></i>
              {id ? `Sale Invoice #${saleNo}` : 'Sale Invoice'}
            </h3>
            <span className={`badge ${id ? 'bg-primary' : 'bg-success bg-opacity-10 text-success border border-success'} px-2 py-1`}>
              {id ? 'EDITING BILL' : 'NEW BILL'}
            </span>
            {items.length > 0 && (
              <span className="badge bg-secondary">
                {items.length} {items.length === 1 ? 'Item' : 'Items'}
              </span>
            )}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            onClick={resetFormToBlank}
            title="Reset Form to Blank"
          >
            <i className="bi bi-arrow-clockwise"></i> New / Blank
          </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => navigate('/sales')}>
            <i className="bi bi-list-ul"></i> Sales List
          </button>
          <button className="btn btn-ocean btn-sm d-flex align-items-center gap-2 shadow-sm" onClick={handleSaveSale} disabled={saving}>
            {saving ? (
              <span className="spinner-border spinner-border-sm" role="status"></span>
            ) : (
              <i className="bi bi-check2-circle fs-6"></i>
            )}
            Save & Print
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small shadow-sm">{error}</div>}
      {successMsg && <div className="alert alert-success py-2 small shadow-sm">{successMsg}</div>}

      {/* 1. Customer & Bill Header Form */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-header bg-light py-2 fw-semibold d-flex justify-content-between align-items-center">
          <span className="form-section-title">
            <i className="bi bi-person-lines-fill"></i> 1. Customer & Invoice Header
          </span>
          {selectedCustomerObj && (
            <span className="badge bg-info bg-opacity-10 text-primary border border-info small">
              Customer Code: #{selectedCustomerObj.Id}
            </span>
          )}
        </div>
        <div className="card-body p-3">
          <div className="row g-3 align-items-start">
            <div className="col-6 col-sm-6 col-md-2 col-lg-2">
              <label className="form-label small fw-semibold text-muted">Bill No *</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light text-muted">#</span>
                <input
                  type="number"
                  className="form-control fw-bold text-primary"
                  value={saleNo}
                  onChange={(e) => setSaleNo(e.target.value)}
                />
              </div>
            </div>

            <div className="col-6 col-sm-6 col-md-2 col-lg-2">
              <label className="form-label small fw-semibold text-muted">Invoice Date *</label>
              <input
                type="date"
                className="form-control form-control-sm fw-semibold"
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
              />
            </div>

            {/* Customer Selection Textbox & List View Dropdown */}
            <div className="col-12 col-md-4 col-lg-4 position-relative" ref={customerDropdownRef}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label small fw-semibold text-muted mb-0">Customer Name *</label>
                {selectedCustomerObj && (
                  <span className={`badge ${previousBalance > 0 ? 'bg-danger text-white' : 'bg-success text-white'}`}>
                    Outstanding: ₹{Math.abs(previousBalance).toFixed(2)} {previousBalance >= 0 ? 'Dr' : 'Cr'}
                  </span>
                )}
              </div>

              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white">
                  <i className="bi bi-person text-primary"></i>
                </span>
                <input
                  type="text"
                  className={`form-control form-control-sm ${customerId ? 'border-primary fw-bold text-dark' : ''}`}
                  placeholder="Enter or search customer name (↓ to navigate)..."
                  value={customerSearch}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCustomerSearch(val);
                    setShowCustomerList(true);
                    setHighlightedCustIndex(0);
                    if (!val) {
                      handleSelectCustomer(null);
                    } else {
                      const matched = customers.find(c => c.AccountName?.toLowerCase().trim() === val.toLowerCase().trim());
                      if (matched) {
                        handleSelectCustomer(matched);
                      } else {
                        setCustomerId('');
                        setSelectedCustomerObj(null);
                        setPreviousBalance(0);
                      }
                    }
                  }}
                  onFocus={() => {
                    setShowCustomerList(true);
                    if (filteredCustomers.length > 0 && highlightedCustIndex === -1) {
                      setHighlightedCustIndex(0);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      if (!showCustomerList) {
                        setShowCustomerList(true);
                        setHighlightedCustIndex(0);
                      } else if (filteredCustomers.length > 0) {
                        setHighlightedCustIndex((prev) => (prev < filteredCustomers.length - 1 ? prev + 1 : 0));
                      }
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      if (filteredCustomers.length > 0) {
                        setHighlightedCustIndex((prev) => (prev > 0 ? prev - 1 : filteredCustomers.length - 1));
                      }
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      if (showCustomerList && filteredCustomers.length > 0) {
                        const targetIdx = highlightedCustIndex >= 0 && highlightedCustIndex < filteredCustomers.length ? highlightedCustIndex : 0;
                        handleSelectCustomer(filteredCustomers[targetIdx]);
                        setShowCustomerList(false);
                        setHighlightedCustIndex(-1);
                        setTimeout(() => productInputRef.current?.focus(), 50);
                      } else {
                        setShowCustomerList(false);
                        productInputRef.current?.focus();
                      }
                    } else if (e.key === 'Escape') {
                      setShowCustomerList(false);
                    }
                  }}
                  required
                />
                {customerSearch && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => {
                      handleSelectCustomer(null);
                      setHighlightedCustIndex(-1);
                    }}
                    title="Clear customer"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>

              {/* Live Search List View Dropdown - Only shown when matching customers exist */}
              {showCustomerList && customerSearch && filteredCustomers.length > 0 && (
                <div
                  className="shadow-lg border rounded bg-white position-absolute mt-1"
                  style={{ zIndex: 1050, width: '100%', minWidth: '320px', maxHeight: '280px', overflowY: 'auto' }}
                >
                  <div className="p-2 bg-light border-bottom d-flex justify-content-between align-items-center">
                    <small className="text-muted fw-semibold">
                      Matching Existing Customers ({filteredCustomers.length}) <span className="text-primary small">Use ↑ ↓ & Enter</span>
                    </small>
                    <button
                      type="button"
                      className="btn-close btn-sm"
                      onClick={() => setShowCustomerList(false)}
                    ></button>
                  </div>
                  <div className="list-group list-group-flush">
                    {filteredCustomers.map((c, idx) => {
                      const isHighlighted = highlightedCustIndex === idx;
                      const isSelected = String(customerId) === String(c.Id);
                      return (
                        <button
                          key={c.Id}
                          type="button"
                          ref={(el) => {
                            if (isHighlighted && el) {
                              el.scrollIntoView({ block: 'nearest' });
                            }
                          }}
                          className={`list-group-item list-group-item-action p-2 d-flex justify-content-between align-items-center ${
                            isHighlighted ? 'bg-primary text-white' : (isSelected ? 'active' : '')
                          }`}
                          onClick={() => {
                            handleSelectCustomer(c);
                            setTimeout(() => productInputRef.current?.focus(), 50);
                          }}
                        >
                          <div>
                            <div className="fw-bold">{c.AccountName}</div>
                            <small className={isHighlighted || isSelected ? 'text-white text-opacity-75' : 'text-muted'}>
                              {c.MobileNo ? `📞 ${c.MobileNo}` : 'No Mobile'} {c.Address ? `• ${c.Address}` : ''}
                            </small>
                          </div>
                          {c.disc > 0 && (
                            <span className={`badge ${isHighlighted ? 'bg-light text-dark' : 'bg-info text-dark'}`}>Disc: {c.disc}%</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Selected Customer Info Badge */}
              {selectedCustomerObj && (
                <div className="mt-1 small text-muted d-flex align-items-center flex-wrap gap-2">
                  <span className="badge bg-success bg-opacity-10 text-success border border-success">
                    <i className="bi bi-check-circle me-1"></i> Existing Customer
                  </span>
                  <span className="fw-bold text-dark">{selectedCustomerObj.AccountName}</span>
                  {customerMobile && <span className="text-muted">({customerMobile})</span>}
                  <span className={`badge ${previousBalance > 0 ? 'bg-danger text-white' : 'bg-success text-white'} px-2 py-1`}>
                    Outstanding Balance: {previousBalance >= 0 ? `₹${previousBalance.toFixed(2)} (Receivable)` : `₹${Math.abs(previousBalance).toFixed(2)} (Advance/Cr)`}
                  </span>
                </div>
              )}
            </div>

            {/* Customer Mobile Number Field */}
            <div className="col-12 col-sm-6 col-md-2 col-lg-2">
              <label className="form-label small fw-semibold text-muted">Customer Mobile</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light text-muted"><i className="bi bi-telephone"></i></span>
                <input
                  type="tel"
                  className="form-control"
                  placeholder="Mobile number"
                  value={customerMobile}
                  onChange={(e) => setCustomerMobile(e.target.value)}
                />
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-2 col-lg-2">
              <label className="form-label small fw-semibold text-muted">Vehicle / Dispatch No</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light text-muted"><i className="bi bi-truck"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. MH-06-AB-1234"
                  value={vehicleNo}
                  onChange={(e) => setVehicleNo(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Line Item Input Console */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-header bg-light py-2 d-flex justify-content-between align-items-center">
          <span className="form-section-title">
            <i className="bi bi-cart-plus-fill"></i>
            2. {editingItemIndex !== null ? `Edit Line #${editingItemIndex + 1}` : 'Add Line Items'}
          </span>
          {editingItemIndex !== null && (
            <span className="badge bg-warning text-dark">
              <i className="bi bi-pencil-fill me-1"></i> Editing Active Row #{editingItemIndex + 1}
            </span>
          )}
        </div>
        <div className="card-body p-3 item-entry-console">
          <div className="row g-2 align-items-end">
            {/* Searchable Product Input with Live Autocomplete & Direct Entry */}
            <div className="col-12 col-md-3 col-lg-3 position-relative" ref={productDropdownRef}>
              <label className="form-label small fw-semibold text-muted mb-1">Product / Item *</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search text-primary"></i>
                </span>
                <input
                  type="text"
                  ref={productInputRef}
                  className={`form-control form-control-sm ${currentItem.productId ? 'border-primary fw-bold text-dark' : ''}`}
                  placeholder="Type product name (↓ to navigate)..."
                  value={currentItem.productName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setShowProductList(true);
                    setHighlightedProdIndex(0);
                    const matched = products.find(p => p.productName?.toLowerCase().trim() === val.toLowerCase().trim());
                    if (matched) {
                      setCurrentItem({
                        ...currentItem,
                        productId: matched.Id,
                        productName: matched.productName,
                        rate: parseFloat(matched.SRate) || currentItem.rate || 0
                      });
                    } else {
                      setCurrentItem({
                        ...currentItem,
                        productId: '',
                        productName: val
                      });
                    }
                  }}
                  onFocus={() => {
                    setShowProductList(true);
                    if (filteredProducts.length > 0 && highlightedProdIndex === -1) {
                      setHighlightedProdIndex(0);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      if (!showProductList) {
                        setShowProductList(true);
                        setHighlightedProdIndex(0);
                      } else if (filteredProducts.length > 0) {
                        setHighlightedProdIndex((prev) => (prev < filteredProducts.length - 1 ? prev + 1 : 0));
                      }
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      if (filteredProducts.length > 0) {
                        setHighlightedProdIndex((prev) => (prev > 0 ? prev - 1 : filteredProducts.length - 1));
                      }
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      if (showProductList && filteredProducts.length > 0) {
                        const targetIdx = highlightedProdIndex >= 0 && highlightedProdIndex < filteredProducts.length ? highlightedProdIndex : 0;
                        const match = filteredProducts[targetIdx];
                        setCurrentItem({
                          ...currentItem,
                          productId: match.Id,
                          productName: match.productName,
                          rate: parseFloat(match.SRate) || currentItem.rate || 0
                        });
                        setShowProductList(false);
                        setHighlightedProdIndex(-1);
                        setTimeout(() => boxesInputRef.current?.focus(), 50);
                      } else {
                        setShowProductList(false);
                        boxesInputRef.current?.focus();
                      }
                    } else if (e.key === 'Escape') {
                      setShowProductList(false);
                    }
                  }}
                  required
                />
                {currentItem.productName && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => {
                      setCurrentItem({
                        ...currentItem,
                        productId: '',
                        productName: '',
                        rate: 0
                      });
                      setHighlightedProdIndex(-1);
                    }}
                    title="Clear product"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>

              {/* Live Product Search Dropdown - Only shown when matching products exist */}
              {showProductList && currentItem.productName && filteredProducts.length > 0 && (
                <div
                  className="shadow-lg border rounded bg-white position-absolute mt-1"
                  style={{ zIndex: 1050, width: '100%', minWidth: '260px', maxHeight: '240px', overflowY: 'auto' }}
                >
                  <div className="p-2 bg-light border-bottom d-flex justify-content-between align-items-center">
                    <small className="text-muted fw-semibold">
                      Matching Fish Items ({filteredProducts.length}) <span className="text-primary small">Use ↑ ↓ & Enter</span>
                    </small>
                    <button
                      type="button"
                      className="btn-close btn-sm"
                      onClick={() => setShowProductList(false)}
                    ></button>
                  </div>
                  <div className="list-group list-group-flush">
                    {filteredProducts.map((p, idx) => {
                      const isHighlighted = highlightedProdIndex === idx;
                      const isSelected = String(currentItem.productId) === String(p.Id);
                      return (
                        <button
                          key={p.Id}
                          type="button"
                          ref={(el) => {
                            if (isHighlighted && el) {
                              el.scrollIntoView({ block: 'nearest' });
                            }
                          }}
                          className={`list-group-item list-group-item-action p-2 d-flex justify-content-between align-items-center ${
                            isHighlighted ? 'bg-primary text-white' : (isSelected ? 'active' : '')
                          }`}
                          onClick={() => {
                            setCurrentItem({
                              ...currentItem,
                              productId: p.Id,
                              productName: p.productName,
                              rate: parseFloat(p.SRate) || currentItem.rate || 0
                            });
                            setShowProductList(false);
                            setHighlightedProdIndex(-1);
                            setTimeout(() => boxesInputRef.current?.focus(), 50);
                          }}
                        >
                          <div className="fw-semibold text-start">{p.productName}</div>
                          <span className={`badge ${isHighlighted ? 'bg-light text-dark' : (isSelected ? 'bg-light text-dark' : 'bg-primary bg-opacity-10 text-primary')} small`}>
                            ₹{parseFloat(p.SRate || 0).toFixed(2)}/kg
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="col-4 col-sm-4 col-md-1 col-lg-1">
              <label className="form-label small fw-semibold text-muted">Boxes</label>
              <input
                type="number"
                ref={boxesInputRef}
                className="form-control form-control-sm text-center fw-semibold text-primary"
                value={currentItem.noofbox}
                onChange={(e) => setCurrentItem({ ...currentItem, noofbox: e.target.value })}
              />
            </div>

            <div className="col-4 col-sm-4 col-md-1 col-lg-1">
              <label className="form-label small fw-semibold text-muted">KG/Box</label>
              <input
                type="number"
                step="0.1"
                className="form-control form-control-sm text-center"
                value={currentItem.kgperbox}
                onChange={(e) => setCurrentItem({ ...currentItem, kgperbox: e.target.value })}
              />
            </div>

            <div className="col-4 col-sm-4 col-md-1 col-lg-1">
              <label className="form-label small fw-semibold text-muted">+Add Qty</label>
              <input
                type="number"
                step="0.1"
                className="form-control form-control-sm text-center"
                value={currentItem.addqty}
                onChange={(e) => setCurrentItem({ ...currentItem, addqty: e.target.value })}
              />
            </div>

            <div className="col-4 col-sm-4 col-md-1 col-lg-1">
              <label className="form-label small fw-semibold text-muted">Total Wt</label>
              <input
                type="text"
                readOnly
                className="form-control form-control-sm bg-light text-center fw-semibold text-primary"
                value={itemTotalWeight.toFixed(2)}
              />
            </div>

            <div className="col-4 col-sm-4 col-md-1 col-lg-1">
              <label className="form-label small fw-semibold text-muted">Net Qty</label>
              <input
                type="text"
                readOnly
                className="form-control form-control-sm bg-white text-center fw-bold text-dark border-primary"
                value={itemNetQty.toFixed(2)}
              />
            </div>

            <div className="col-4 col-sm-4 col-md-1 col-lg-1">
              <label className="form-label small fw-semibold text-muted">Rate (₹) *</label>
              <input
                type="number"
                step="0.01"
                ref={rateInputRef}
                className="form-control form-control-sm text-end fw-bold text-dark"
                value={currentItem.rate}
                onChange={(e) => setCurrentItem({ ...currentItem, rate: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddItem(e);
                  }
                }}
              />
            </div>

            <div className="col-6 col-sm-6 col-md-1 col-lg-1">
              <label className="form-label small fw-semibold text-muted">Amount (₹)</label>
              <input
                type="text"
                readOnly
                className="form-control form-control-sm bg-light text-end fw-bold text-success border-success"
                value={itemTotalAmount.toFixed(2)}
                title="Line Total = Net Qty * Rate"
              />
            </div>

            <div className="col-6 col-sm-6 col-md-2 col-lg-2 d-flex gap-1">
              <button
                type="button"
                className={`btn btn-sm ${editingItemIndex !== null ? 'btn-warning' : 'btn-teal'} flex-grow-1 d-flex align-items-center justify-content-center gap-1 shadow-sm`}
                onClick={handleAddItem}
              >
                <i className={editingItemIndex !== null ? 'bi bi-check-lg' : 'bi bi-plus-lg'}></i>
                {editingItemIndex !== null ? 'Update Item' : 'Add Item'}
              </button>
              {editingItemIndex !== null && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setEditingItemIndex(null);
                    setCurrentItem({
                      productId: '',
                      productName: '',
                      noofbox: 0,
                      kgperbox: 0,
                      addqty: 0,
                      disc_qty: 0,
                      rate: 0
                    });
                  }}
                  title="Cancel Edit"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cart Items Table */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered table-hover table-sm align-middle mb-0">
              <thead className="table-light text-center small">
                <tr>
                  <th style={{ width: '40px' }}>#</th>
                  <th className="text-start">Product / Item</th>
                  <th>Boxes</th>
                  <th>KG/Box</th>
                  <th>+Add Qty</th>
                  <th>Total Wt (kg)</th>
                  <th>Net Qty (kg)</th>
                  <th className="text-end">Rate (₹)</th>
                  <th className="text-end">Total Amount (₹)</th>
                  <th style={{ width: '80px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted small">
                      <i className="bi bi-inbox fs-3 d-block text-secondary mb-1"></i>
                      No items added to invoice yet. Select a product above and click "Add Item".
                    </td>
                  </tr>
                ) : (
                  items.map((it, idx) => (
                    <tr key={idx} className={`text-center small ${editingItemIndex === idx ? 'table-warning' : ''}`}>
                      <td className="text-muted">{idx + 1}</td>
                      <td className="text-start fw-bold text-dark">{it.productName}</td>
                      <td className="fw-semibold text-primary">{it.noofbox}</td>
                      <td>{it.kgperbox}</td>
                      <td>{it.addqty}</td>
                      <td className="fw-semibold text-secondary">{parseFloat(it.Qty).toFixed(2)}</td>
                      <td className="fw-bold text-dark bg-light">{parseFloat(it.net_qty).toFixed(2)}</td>
                      <td className="text-end fw-semibold">₹{parseFloat(it.rate).toFixed(2)}</td>
                      <td className="text-end fw-bold text-success">₹{parseFloat(it.total).toFixed(2)}</td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary py-0 px-2"
                            onClick={() => handleEditItem(idx)}
                            title="Edit this line"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger py-0 px-2"
                            onClick={() => handleRemoveItem(idx)}
                            title="Remove this line"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {items.length > 0 && (
                <tfoot className="table-light small fw-bold">
                  <tr className="text-center">
                    <td colSpan="2" className="text-end text-muted">Summary Totals:</td>
                    <td className="text-primary">{items.reduce((s, it) => s + (parseFloat(it.noofbox) || 0), 0)} Boxes</td>
                    <td></td>
                    <td></td>
                    <td className="text-secondary">{items.reduce((s, it) => s + (parseFloat(it.Qty) || 0), 0).toFixed(2)} kg</td>
                    <td className="text-dark">{items.reduce((s, it) => s + (parseFloat(it.net_qty) || 0), 0).toFixed(2)} kg</td>
                    <td className="text-end text-muted">Subtotal:</td>
                    <td className="text-end text-success fs-6">₹{subTotal.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* Calculation & Additional Charges Grid */}
      <div className="row g-3 mb-4">
        {/* Left Side Adjustments */}
        <div className="col-12 col-lg-7">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-light py-2 fw-semibold">
              <span className="form-section-title">
                <i className="bi bi-sliders"></i> 3. Additional Charges & Deductions
              </span>
            </div>
            <div className="card-body p-3">
              {/* Discount Row */}
              <div className="row g-2 align-items-center mb-3 p-2 bg-light rounded border">
                <div className="col-12 col-sm-4 fw-semibold text-secondary small">
                  <i className="bi bi-tag-fill text-danger me-1"></i> Discount
                </div>
                <div className="col-6 col-sm-4">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">Disc %</span>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control text-end"
                      value={discPercent}
                      onChange={(e) => {
                        setDiscPercent(e.target.value);
                        setDiscAmount((subTotal * (parseFloat(e.target.value) || 0)) / 100);
                      }}
                    />
                  </div>
                </div>
                <div className="col-6 col-sm-4">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">₹ Total</span>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control text-end fw-bold text-danger"
                      value={discAmount}
                      onChange={(e) => setDiscAmount(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Loading Charges Row (formerly Hamali) */}
              <div className="row g-2 align-items-center mb-3">
                <div className="col-12 col-sm-4 fw-semibold text-secondary small">
                  <i className="bi bi-box-seam-fill text-primary me-1"></i> Loading Charges
                </div>
                <div className="col-4 col-sm-3">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">Unit</span>
                    <input
                      type="number"
                      className="form-control text-end"
                      value={hamaliUnit}
                      onChange={(e) => setHamaliUnit(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-4 col-sm-2">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">Rate</span>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control text-end"
                      value={hamaliRate}
                      onChange={(e) => setHamaliRate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-4 col-sm-3">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">₹ Total</span>
                    <input
                      type="text"
                      readOnly
                      className="form-control text-end bg-light fw-bold"
                      value={calculatedHamali.toFixed(2)}
                    />
                  </div>
                </div>
              </div>

              {/* Ice Row */}
              <div className="row g-2 align-items-center mb-3">
                <div className="col-12 col-sm-4 fw-semibold text-secondary small">
                  <i className="bi bi-snow text-info me-1"></i> Ice Charges
                </div>
                <div className="col-4 col-sm-3">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">Unit</span>
                    <input
                      type="number"
                      className="form-control text-end"
                      value={iceUnit}
                      onChange={(e) => setIceUnit(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-4 col-sm-2">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">Rate</span>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control text-end"
                      value={iceRate}
                      onChange={(e) => setIceRate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-4 col-sm-3">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">₹ Total</span>
                    <input
                      type="text"
                      readOnly
                      className="form-control text-end bg-light fw-bold"
                      value={calculatedIce.toFixed(2)}
                    />
                  </div>
                </div>
              </div>

              {/* Transport & Expenses Row */}
              <div className="row g-2 align-items-center mb-3">
                <div className="col-12 col-sm-4 fw-semibold text-secondary small">
                  <i className="bi bi-truck text-warning me-1"></i> Transport & Expenses
                </div>
                <div className="col-6 col-sm-4">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">Transport ₹</span>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control text-end"
                      value={transportCharges}
                      onChange={(e) => setTransportCharges(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-6 col-sm-4">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">Expenses ₹</span>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control text-end"
                      value={expenses}
                      onChange={(e) => setExpenses(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div className="row g-2 align-items-center">
                <div className="col-12 col-sm-4 fw-semibold text-secondary small">
                  <i className="bi bi-chat-left-text me-1"></i> Bill Remarks
                </div>
                <div className="col-12 col-sm-8">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Enter notes or instructions..."
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side Summary & Balance Sheet */}
        <div className="col-12 col-lg-5">
          <div className="summary-panel-card bg-white h-100">
            <div className="summary-panel-header d-flex justify-content-between align-items-center">
              <span><i className="bi bi-wallet2 me-2"></i> 4. Settlement & Ledger Sync</span>
              <span className="badge bg-white text-dark small">₹ INR</span>
            </div>
            <div className="card-body p-0">
              <div className="summary-row">
                <span className="text-muted">Items Subtotal:</span>
                <span className="fw-bold">₹{subTotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span className="text-muted">Discounts (Deductions):</span>
                <span className="text-danger fw-semibold">
                  -₹{(parseFloat(discAmount) || calculatedDiscount).toFixed(2)}
                </span>
              </div>
              <div className="summary-row">
                <span className="text-muted">Loading + Ice + Transport:</span>
                <span className="text-success fw-semibold">
                  +₹{(calculatedHamali + calculatedIce + (parseFloat(transportCharges) || 0) + (parseFloat(expenses) || 0)).toFixed(2)}
                </span>
              </div>

              <div className="summary-row grand-total">
                <span>Grand Total (Bill):</span>
                <span>₹{grandTotal.toFixed(2)}</span>
              </div>

              <div className="p-3 bg-light border-top border-bottom">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted fw-semibold">Customer Previous Balance:</span>
                  <span className={`badge ${previousBalance > 0 ? 'bg-danger bg-opacity-10 text-danger border border-danger' : 'bg-success bg-opacity-10 text-success border border-success'} fs-6`}>
                    {previousBalance >= 0 ? `₹${previousBalance.toFixed(2)} (Dr)` : `₹${Math.abs(previousBalance).toFixed(2)} (Cr)`}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="small fw-bold text-dark">Payment Received Today (₹):</label>
                  <div className="input-group input-group-sm w-50">
                    <span className="input-group-text bg-success text-white fw-bold">₹</span>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control text-end fw-bold text-success border-success"
                      placeholder="0.00"
                      value={paidAmt}
                      onChange={(e) => setPaidAmt(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="summary-row balance-due">
                <span>Net Closing Balance:</span>
                <span>
                  ₹{netBalanceAfterSale.toFixed(2)}
                  <small className="fs-6 ms-1 fw-semibold text-muted">
                    {netBalanceAfterSale >= 0 ? '(Receivable)' : '(Advance)'}
                  </small>
                </span>
              </div>

              <div className="p-3">
                <button
                  type="button"
                  className="btn btn-ocean btn-lg w-100 shadow-sm d-flex align-items-center justify-content-center gap-2"
                  onClick={handleSaveSale}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    <i className="bi bi-printer-fill fs-5"></i>
                  )}
                  Save & Generate Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Full List View Modal */}
      {showCustomerModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header bg-primary text-white py-2">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-people-fill me-2"></i> Select Customer (List View)
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowCustomerModal(false)}
                ></button>
              </div>
              <div className="modal-body p-3">
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by customer name, mobile, address..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>#</th>
                        <th>Customer Name</th>
                        <th>Mobile</th>
                        <th>Address</th>
                        <th>Disc %</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCustomers.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4 text-muted">
                            No matching customers found.
                          </td>
                        </tr>
                      ) : (
                        filteredCustomers.map((c, idx) => (
                          <tr
                            key={c.Id}
                            className={String(customerId) === String(c.Id) ? 'table-primary fw-bold' : ''}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelectCustomer(c)}
                          >
                            <td>{idx + 1}</td>
                            <td className="text-primary fw-bold">{c.AccountName}</td>
                            <td>{c.MobileNo || '-'}</td>
                            <td>{c.Address || '-'}</td>
                            <td>{c.disc ? `${c.disc}%` : '0%'}</td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-ocean py-0 px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectCustomer(c);
                                }}
                              >
                                Select
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer py-2 bg-light">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCustomerModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Customer Modal */}
      {showQuickAddCustModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header bg-success text-white py-3">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-person-plus-fill me-2"></i> Quick Add New Customer
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowQuickAddCustModal(false)}
                ></button>
              </div>
              <form onSubmit={handleQuickAddCustomer}>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-muted">
                      Customer Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light"><i className="bi bi-person-fill"></i></span>
                      <input
                        type="text"
                        className="form-control fw-bold"
                        placeholder="e.g. Ramesh Patil / Sagar Fish Traders"
                        value={quickCustName}
                        onChange={(e) => setQuickCustName(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-muted">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light"><i className="bi bi-telephone-fill"></i></span>
                      <input
                        type="tel"
                        className="form-control"
                        placeholder="10-digit Mobile No"
                        value={quickCustMobile}
                        onChange={(e) => setQuickCustMobile(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-muted">Address / City</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Dadar Market, Mumbai"
                      value={quickCustAddress}
                      onChange={(e) => setQuickCustAddress(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-muted">Default Discount (%)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control"
                      placeholder="0.0"
                      value={quickCustDisc}
                      onChange={(e) => setQuickCustDisc(e.target.value)}
                    />
                  </div>
                </div>

                <div className="modal-footer py-2 bg-light">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowQuickAddCustModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success d-flex align-items-center gap-2" disabled={quickCustSaving}>
                    {quickCustSaving ? (
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                    ) : (
                      <i className="bi bi-check-circle-fill"></i>
                    )}
                    Save & Select Customer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Printable Invoice Modal */}
      {showPrintModal && printData && (
        <InvoicePrintModal
          invoiceData={printData}
          type="SALE"
          onClose={() => {
            setShowPrintModal(false);
          }}
        />
      )}
    </div>
  );
};

export default SaleEntry;
